import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const VIEWPORT_START_OFFSET = 50;

/** Keep `start` in sync with scrollStartAlreadyMet() or visible content can sit between the two thresholds (desktop). */
function scrollTriggerDefaults() {
	return {
		start: `top bottom-=${VIEWPORT_START_OFFSET}px`,
		once: true,
		invalidateOnRefresh: true,
	};
}
const IN_VIEW_LOAD_DELAY = 1.5;

/** True when `el` has already passed the ScrollTrigger start line (would fire immediately on load). */
function scrollStartAlreadyMet(el) {
	const line = window.innerHeight - VIEWPORT_START_OFFSET;
	return el.getBoundingClientRect().top <= line;
}

/** ScrollTrigger ignores `delay` on tweens; use timeline start position instead. */
function entrancePosition(triggerEl) {
	return scrollStartAlreadyMet(triggerEl) ? IN_VIEW_LOAD_DELAY : 0;
}

export default class BlockBoost {
	constructor(block) {
		this.block = block;
		this.title = this.block.querySelector(".block-title");
		this.titleParts = this.title ? Array.from(this.title.children) : [];
		this.titleIcon = this.title ? this.title.querySelector(".block-icon") : null;
		this.titleText = this.title ? this.title.querySelector(".block-title-text") : null;
		this.text = this.block.querySelector(".block-intro-text");
		this.textBlocks = this.text ? Array.from(this.text.children) : [];
		this.linksWrap = this.block.querySelector(".block-links");
		this.links = this.block.querySelectorAll(".block-links .btn");
		this.supportBox = this.block.querySelector(".boost-support-box");
		this.cloud1 = this.supportBox ? this.supportBox.querySelector(".cloud-1") : null;
		this.cloud2 = this.supportBox ? this.supportBox.querySelector(".cloud-2") : null;
		this.cloud3 = this.supportBox ? this.supportBox.querySelector(".cloud-3") : null;
		this.cloud4 = this.supportBox ? this.supportBox.querySelector(".cloud-4") : null;
		this.cloud5 = this.supportBox ? this.supportBox.querySelector(".cloud-5") : null;
		this.seagull = this.supportBox ? this.supportBox.querySelector(".seagull") : null;
		this.rocketTrail = this.supportBox ? this.supportBox.querySelector(".rocket-trail") : null;
		this.terms = this.block.querySelector(".block-terms");
		this.generalDescription = this.block.querySelector(".block-general-description");
		this.categoriesNav = this.block.querySelector(".boost-categories");
		this.categoriesNavItems = this.categoriesNav ? Array.from(this.categoriesNav.children) : [];


		this.setStartingPositions();
		this.animateIn();
		this.scrollEffects();
	}

	setStartingPositions() {
		if (this.titleParts.length) {
			if (this.titleIcon) {
				gsap.set(this.titleIcon, { opacity: 0, x: -64 });
			}
			if (this.titleText) {
				gsap.set(this.titleText, { opacity: 0, x: 64 });
			}
		} else if (this.title) {
			gsap.set(this.title, { opacity: 0, x: -64 });
		}
		if (this.textBlocks.length) {
			gsap.set(this.textBlocks, { opacity: 0, y: 64 });
		}
		if (this.links.length) {
			gsap.set(this.links, { opacity: 0, x: -64 });
		}
		if (this.categoriesNavItems.length) {
			gsap.set(this.categoriesNavItems, { opacity: 0, x: -64 });
		}
		if (this.supportBox) {
			gsap.set(this.supportBox, { opacity: 0, y: 64 });
		}
		if (this.terms) {
			gsap.set(this.terms, { opacity: 0, y: 64 });
		}
		if (this.generalDescription) {
			gsap.set(this.generalDescription, { opacity: 0, y: 64 });
		}
		if (this.seagull) {
			gsap.set(this.seagull, { right: "80%"});
		}
		if (this.rocketTrail) {
			gsap.set(this.rocketTrail, { width: 0 });
		}
	}

	animateIn() {
		if (this.title) {
			const pos = entrancePosition(this.title);
			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: this.title,
					start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
				},
			});
			if (this.titleParts.length) {
				tl.to(
					this.titleParts,
					{
						opacity: 1,
						x: 0,
						duration: 0.5,
						stagger: 0.2,
						ease: "power2.out",
					},
					pos
				);
			} else {
				tl.to(
					this.title,
					{
						opacity: 1,
						x: 0,
						duration: 0.5,
						ease: "power2.out",
					},
					pos
				);
			}
		}

		if (this.text && this.textBlocks.length) {
			const pos = entrancePosition(this.text);
			gsap
				.timeline({
					scrollTrigger: {
						trigger: this.text,
						start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
					},
				})
				.to(
					this.textBlocks,
					{
						opacity: 1,
						y: 0,
						duration: 0.75,
						stagger: 0.08,
						ease: "power2.out",
					},
					pos
				);
		}

		if (this.links.length && this.linksWrap) {
			const pos = entrancePosition(this.linksWrap);
			gsap
				.timeline({
					scrollTrigger: {
						trigger: this.linksWrap,
						start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
					},
				})
				.to(
					this.links,
					{
						opacity: 1,
						x: 0,
						duration: 0.5,
						stagger: 0.2,
						ease: "power2.out",
					},
					pos
				);
		}

		if (this.categoriesNav && this.categoriesNavItems.length) {
			const pos = entrancePosition(this.categoriesNav);
			gsap.timeline({
				scrollTrigger: {
					trigger: this.categoriesNav,
					start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
				},
			}).to(this.categoriesNavItems, {
				opacity: 1,
				x: 0,
				duration: 0.5,
				stagger: 0.2,
				ease: "power2.out",
			}, pos);
		}

		if (this.supportBox) {
			// Support is at the bottom of the block; its top often hasn't crossed the same
			// start line on load even when the offer is on-screen. Match load-delay to the root block.
			const pos = entrancePosition(this.block);
			gsap
				.timeline({
					scrollTrigger: {
						trigger: this.supportBox,
						start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
					},
				})
				.to(
					this.supportBox,
					{
						opacity: 1,
						y: 0,
						duration: 0.5,
						ease: "power2.out",
					},
					pos
				);
		}

		if (this.terms) {
			const pos = entrancePosition(this.terms);
			gsap.timeline({
				scrollTrigger: {
					trigger: this.terms,
					start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
				},
			}).to(this.terms, {
				opacity: 1,
				y: 0,
				duration: 0.5,
				ease: "power2.out",
			}, pos);
		}

		if (this.generalDescription) {
			const pos = entrancePosition(this.generalDescription);
			gsap.timeline({
				scrollTrigger: {
					trigger: this.generalDescription,
					start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
				},
			}).to(this.generalDescription, {
				opacity: 1,
				y: 0,
				duration: 0.5,
				ease: "power2.out",
			}, pos);
		}

		// Seagull and rocket trail
		if (this.seagull) {
			const pos = entrancePosition(this.seagull);
			gsap.timeline({
				scrollTrigger: {
					trigger: this.seagull,
					start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
				},
			}).to(this.seagull, {
				right: "0%",
				duration: 2,
				ease: "power3.inOut",
			}, pos);
		}
		if (this.rocketTrail) {
			const pos = entrancePosition(this.rocketTrail);
			gsap.timeline({
				scrollTrigger: {
					trigger: this.seagull,
					start: scrollTriggerDefaults().start,
					once: true,
					invalidateOnRefresh: true,
				},
			}).to(this.rocketTrail, {
				width: "80%",
				duration: 2,
				ease: "power3.inOut",
			}, pos);
		}
	}

	scrollEffects() {
		const clouds = {
			cloud1: {el: this.cloud1, speed: 0.04},
			cloud2: {el: this.cloud2, speed: 0.015},
			cloud3: {el: this.cloud3, speed: 0.05},
			cloud4: {el: this.cloud4, speed: 0.02},
			// cloud5: {el: this.cloud5, speed: 0.025},
		};
		if (Object.keys(clouds).length) {
			Object.values(clouds).forEach(({ el, speed }) => {
				const cloudyTo = gsap.quickTo(el, "y", { 
					duration: 2, 
					ease: "power3" 
				});
				ScrollTrigger.create({
					onUpdate: (self) => {
						cloudyTo(self.getVelocity() * speed);
					}
				});
			});
		}
	}
}
